
import Vue from 'vue';

import { Component, Prop, Watch } from 'vue-property-decorator';
import { Get } from 'vuex-pathify';

import { FulfillmentType } from '../../../types/ProductDelivery';
import { IEligibleSubscriptionResource, IPendingSubscriptionResource, ProductSubscriptionStatus } from 'JS/types/Order';
import { ILocalStoreJson } from '../../../types/LocalStore';
import { IProductGroupSelector } from '../../../types/Product';

import BRadioCard from 'JS/components/blain-form/BRadioCard.vue';
import BRadioGroup from 'JS/components/blain-form/BRadioGroup.vue';
import FulfillmentOptionLink from './FulfillmentOptionLink.vue';
import GlobalData from 'JS/global/window-data';
import Icon from 'Src/js/components/Icon.vue';
import LinkButton from 'JS/components/LinkButton.vue';
import StockSubscriptionForm from './StockSubscriptionForm.vue';
import StockSubscriptionFormTrigger from './StockSubscriptionFormTrigger.vue';
import TransitionExpand from 'JS/components/transitions/TransitionExpand.vue';
import { BSelect } from 'JS/components/blain-form';
import { IProductDetailProductJson } from 'JS/types/PDP';


@Component({
	name: 'InStorePickup',
	components: {
		BRadioCard,
		BRadioGroup,
		BSelect,
		FulfillmentOptionLink,
		Icon,
		LinkButton,
		TransitionExpand,
		StockSubscriptionForm,
		StockSubscriptionFormTrigger,

	}
})
export default class SubscriptionOptions extends Vue {

	/* Computed
	============================================*/

	@Prop({ type: String })
	readonly currentFulfillmentType: string;

	@Prop({ type: String })
	readonly currentFrequencySelection: string;

	@Prop({ type: Boolean, required: false, default: false })
	readonly loading: boolean;

	@Get('customerEmail')
	readonly customerEmail: string;

	@Get('outOfMarket')
	readonly outOfMarket: boolean;

	@Get('localStore')
	readonly localStore: ILocalStoreJson;

	@Get('product')
	readonly product: IProductDetailProductJson;

	@Get('remainingSelectors')
	readonly remainingSelectors: IProductGroupSelector[];

	@Get('fetchingProduct')
	readonly fetchingProduct: boolean;

	@Get('selectedFulfillmentType')
	readonly selectedFulfillmentType: FulfillmentType;

	@Get('isInStockInStore')
	readonly isInStockInStore: boolean;

	@Get('isPickupDisabled')
	readonly isPickupDisabled: boolean;

	@Get('hasSelectorError')
	readonly hasSelectorError: boolean;

	@Get('hasSelectedVariant')
	readonly hasSelectedVariant: boolean;

	@Get('quantity')
	readonly quantity: number;

	get currentSubscriptionSelection() {
		return this.subscriptionSelection;
	}

	set currentSubscriptionSelection(newSelection: FulfillmentType.InStorePickup | 'Subscription') {
		this.subscriptionSelection = newSelection;
		this.$emit('change', FulfillmentType.InStorePickup);
	}

	get inStoreStockMessage(): string {
		if (this.product.notCarriedInStore) return 'Not Carried';
		if (this.isInStockInStore) return this.product.activeInStorePickup ? 'In Stock' : 'Available in Store';
		return 'Out of Stock';
	};

	get stockSubscriptionFormEnabled(): boolean {
		return this.product.backInStockInfo.isProductEligible &&
				!this.isInStockInStore &&
				this.hasSelectedVariant &&
				!this.outOfMarket &&
				!this.fetchingProduct &&
				!this.product.notCarriedInStore;
	};

	get linkToStockModalText(): string {
		return this.isInStockInStore ? 'Change Store' : 'Check Nearby Stores';
	};

	get subFrequencySelection(): string {
		return this.currentFrequencySelection;
	}

	set subFrequencySelection(newVal: string) {
		this.$emit('update:currentFrequencySelection', newVal);
	}

	get isDisabled() {
		return this.isPickupDisabled || !this.hasSelectedVariant || this.loading;
	}

	get isRewardsMember() {
		return GlobalData.customer?.isRewardsMember;
	}

	get subscriptionInfo(): IEligibleSubscriptionResource | IPendingSubscriptionResource | null {
		if (this.product.subscriptionInfo.status === ProductSubscriptionStatus.Eligible
			|| this.product.subscriptionInfo.status === ProductSubscriptionStatus.Pending) {
			return this.product.subscriptionInfo ;
		}
		return null;
	}

	get inStorePickup() {
		return FulfillmentType.InStorePickup;
	}

	get fulfillmentRadioName(): string {
		return `fulfillment-${this.product.blainNumber}`;
	}

	/* Data
	============================================*/
	subscriptionSelection: FulfillmentType.InStorePickup | 'Subscription' = null;
	isStockSubscriptionFormShowing: boolean = false;
	prevFrequencySelection: string = "";
	/* Methods
	============================================*/

	toggleStockSubscriptionForm() {
		this.isStockSubscriptionFormShowing = !this.isStockSubscriptionFormShowing;
	}

	farmsBenefitLineItem(str: string, substr: string, adjustWithQty?: boolean): string {
		let finalStr = substr;
		if (adjustWithQty) {
			finalStr = (parseFloat(substr) * this.quantity).toFixed(2);
		}
		return str.replaceAll(substr, `<strong>${finalStr}</strong>`);
	}

	/* Lifecycle Hooks
	============================================*/

	created() {
		if (this.isDisabled) {
			return;
		}
		if (this.subscriptionInfo.status === ProductSubscriptionStatus.Pending) {
			this.subscriptionSelection = 'Subscription';
		} else if (this.currentFulfillmentType === FulfillmentType.InStorePickup) {
			this.subscriptionSelection = FulfillmentType.InStorePickup;
		}
	}

	/* Watchers
	============================================*/

	@Watch('currentFulfillmentType')
	onFulfillmentTypeChange(currentFulfillmentType: string) {
		if (!(currentFulfillmentType === FulfillmentType.InStorePickup || currentFulfillmentType === 'Subscription')) {
			this.subscriptionSelection = null;
		}
	}

	@Watch('subscriptionSelection')
	onSubscriptionSelectionChange(currentSubscriptionSelection: string) {
		if (currentSubscriptionSelection !== 'Subscription') {
			this.prevFrequencySelection = this.subFrequencySelection;
			this.subFrequencySelection = "";
		} else {
			if (this.prevFrequencySelection) {
				this.subFrequencySelection = this.prevFrequencySelection;
			} else {
				this.subFrequencySelection = this.subscriptionInfo.frequency.toString();
			}
		}
	}
}
